import React, { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ArrowTrendingUpIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import ExitPlanOverlay from './ExitPlanOverlay';

const UPDATE_CURRENT_USER_DECISION = gql`
  mutation updateCurrentUser(
    $growth_decision_at: ISO8601DateTime
    $exit_decision_at: ISO8601DateTime
    $snoozed_growth_exit_prompt_at: ISO8601DateTime
    $growth_exit_snoozed_until: ISO8601DateTime
    $permanently_snoozed_growth_exit_prompt: Boolean
  ) {
    updateCurrentUser(
      growth_decision_at: $growth_decision_at
      exit_decision_at: $exit_decision_at
      snoozed_growth_exit_prompt_at: $snoozed_growth_exit_prompt_at
      growth_exit_snoozed_until: $growth_exit_snoozed_until
      permanently_snoozed_growth_exit_prompt: $permanently_snoozed_growth_exit_prompt
    )
  }
`;

// Queries to fetch active sprint end date for snoozing when user selects Grow
const GET_CURRENT_USER_FOR_SPRINT = gql`
  query GetCurrentUserForGxPrompt {
    getCurrentUser { id }
  }
`;

const GET_SPRINTS = gql`
  query GetActiveSprintForGxPrompt($ownerId: ID, $status: [SprintStatusEnum!]) {
    sprints(owner_user_id: $ownerId, status: $status) { id status endOn }
  }
`;

const GrowthExitDecisionCard = ({ user, onCompleted }) => {
  const [mode, setMode] = useState('prompt'); // 'prompt' | 'confirmed'
  const [updateCurrentUser, { loading }] = useMutation(UPDATE_CURRENT_USER_DECISION);

  // Fetch active sprint end date (lightweight, cache-first)
  const { data: cu } = useQuery(GET_CURRENT_USER_FOR_SPRINT, { fetchPolicy: 'cache-first' });
  const ownerId = cu?.getCurrentUser?.id;
  const { data: sData } = useQuery(GET_SPRINTS, { variables: { ownerId, status: ['ACTIVE'] }, skip: !ownerId, fetchPolicy: 'cache-first' });
  const activeSprintEnd = useMemo(() => {
    try {
      const sprints = sData?.sprints || [];
      const active = sprints.find((s) => String(s.status).toUpperCase() === 'ACTIVE');
      return active?.endOn ? new Date(active.endOn) : null;
    } catch (_) { return null; }
  }, [sData]);

  const track = (action, extra = {}) => {
    try { if (window.ahoy?.track) window.ahoy.track('GrowthExitPrompt', { action, ...extra }); } catch (_) {}
  };

  const handleGrow = async () => {
    track('grow');
    const now = new Date();
    // If there is an active sprint, snooze until it ends; else default to 30 days
    const until = (activeSprintEnd instanceof Date && !isNaN(activeSprintEnd))
      ? activeSprintEnd
      : new Date(Date.now() + 30 * 24 * 3600 * 1000);
    try {
      await updateCurrentUser({ variables: {
        growth_decision_at: now.toISOString(),
        snoozed_growth_exit_prompt_at: now.toISOString(),
        growth_exit_snoozed_until: until.toISOString(),
      } });
    } catch (_) {}
    setMode('confirmed');
    setTimeout(() => { try { onCompleted && onCompleted('grow'); } catch (_) {} }, 3600);
  };

  const [overlayOpen, setOverlayOpen] = useState(false);
  const handleExit = async () => {
    track('exit');
    // Open overlay immediately to ensure sprint creation mutation runs regardless of re-renders
    setOverlayOpen(true);
    // Record decision timestamp in parallel (non-blocking for overlay)
    try { await updateCurrentUser({ variables: { exit_decision_at: new Date().toISOString() } }); } catch (_) {}
  };

  const handleSnooze = async (type) => {
    const nowIso = new Date().toISOString();
    if (type === '30d') {
      const until = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
      await updateCurrentUser({ variables: { snoozed_growth_exit_prompt_at: nowIso, growth_exit_snoozed_until: until } });
      track('snoozed', { until: '30d' });
    } else if (type === 'after_sprint') {
      // Without sprint end exposure here, use 30d as a pragmatic default; can be replaced if end date available
      const until = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
      await updateCurrentUser({ variables: { snoozed_growth_exit_prompt_at: nowIso, growth_exit_snoozed_until: until } });
      track('snoozed', { until: 'after_sprint' });
    } else if (type === 'never') {
      await updateCurrentUser({ variables: { snoozed_growth_exit_prompt_at: nowIso, permanently_snoozed_growth_exit_prompt: true } });
      track('snoozed', { until: 'never' });
    }
    setMode('confirmed');
    setTimeout(() => { try { onCompleted && onCompleted('snoozed'); } catch (_) {} }, 3600);
  };

  if (mode === 'confirmed') {
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 border border-succession-border" data-tutorial-id="whats-next">
        <div className="px-4 py-4 sm:px-6 sm:py-5 h-full flex flex-col items-center justify-center text-center">
          <ArrowTrendingUpIcon className="h-12 w-12 text-succession-primary mb-3" />
          <div className="text-lg text-succession-text-primary">Great. Keep making progress on your current sprint and master plan. I’ll check in again later.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 border border-succession-border" data-tutorial-id="whats-next">
      <div className="px-4 py-4 sm:px-6 sm:py-5 h-full flex flex-col">
        <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>What's Next for You?</h3>
        <div className="text-succession-text-secondary text-sm mt-2">Based on your recent progress, are you focused on growing your business — or preparing for your exit?</div>
        <div className="mt-4 flex flex-col gap-2">
          <button type="button" className="inline-flex items-center justify-center px-3 py-2 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover" onClick={handleGrow} disabled={loading}>
            <ArrowTrendingUpIcon className="h-4 w-4 mr-2 text-white" />
            <span>Grow Your Business</span>
          </button>
          <button type="button" className="inline-flex items-center justify-center px-3 py-2 rounded-md border border-succession-border text-succession-text-primary hover:bg-succession-hover-bg" onClick={handleExit} disabled={loading}>
            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2 text-succession-text-primary" />
            <span>Plan Your Exit</span>
          </button>
        </div>
        <div className="mt-4 pt-3 border-t border-succession-border">
          <div className="text-xs text-succession-text-secondary mb-2">Snooze</div>
          <div className="flex flex-col gap-2">
            <button type="button" className="text-xs underline text-succession-text-secondary hover:text-succession-text-primary text-left" onClick={() => handleSnooze('30d')} disabled={loading}>Ask me again in 30 days</button>
            <button type="button" className="text-xs underline text-succession-text-secondary hover:text-succession-text-primary text-left" onClick={() => handleSnooze('after_sprint')} disabled={loading}>Ask me after current sprint</button>
            <button type="button" className="text-xs underline text-succession-text-secondary hover:text-succession-text-primary text-left" onClick={() => handleSnooze('never')} disabled={loading}>Don’t show again</button>
          </div>
        </div>
      </div>
      <ExitPlanOverlay isOpen={overlayOpen} onClose={() => setOverlayOpen(false)} />
    </div>
  );
};

export default GrowthExitDecisionCard;


